import { createSelector, type PayloadAction } from '@reduxjs/toolkit'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type { FactoryCodeAttributes } from '#api/tmsApiService'
import type { MarketWithNationalCodes } from '#features/NationalCodes/loadNationalCodes'
import type { RootState } from '../../root'
import type { TruckConfiguration, TruckConfigurations } from '../configurationSlice'

export type MarketDetails = MarketWithNationalCodes
export type MarketAttributes = FactoryCodeAttributes[]
export type MarketEntry = MarketData[EnhancedMarketId]

export type MarketData = Partial<
  Record<
    EnhancedMarketId,
    {
      details: MarketDetails | undefined
      attributes: MarketAttributes
    }
  >
>

export const marketDataReducers = {
  addMarketDetailsAndAttributes: (
    state: TruckConfigurations,
    {
      payload: { marketId, attributes, details },
    }: PayloadAction<{ marketId: EnhancedMarketId; attributes: FactoryCodeAttributes[]; details: MarketWithNationalCodes | undefined }>,
  ) => {
    const market = state.marketData[marketId] ?? { attributes, details }
    market.attributes = attributes
    state.marketData[marketId] = market
  },
  addMarketAttributes: (
    state: TruckConfigurations,
    {
      payload: { marketId, attributes },
    }: PayloadAction<{
      marketId: EnhancedMarketId
      attributes: FactoryCodeAttributes[]
    }>,
  ) => {
    const market = state.marketData[marketId] ?? { attributes, details: undefined }
    market.attributes = attributes
    state.marketData[marketId] = market
  },
  addMarketDetails: (
    state: TruckConfigurations,
    { payload: { marketId, details } }: PayloadAction<{ marketId: EnhancedMarketId; details: MarketWithNationalCodes }>,
  ) => {
    const market = state.marketData[marketId] ?? { details, attributes: [] }
    market.details = details
    state.marketData[marketId] = market
  },
}

export const selectMarketById = createSelector(
  [(state: RootState) => state.truckConfigurations.marketData, (_state: RootState, marketId?: EnhancedMarketId) => marketId],
  (marketData, marketId): MarketData[EnhancedMarketId] => (marketId ? marketData[marketId] : undefined),
)

export const selectMarketByConfiguration = createSelector(
  [
    (state: RootState) => state.truckConfigurations.marketData,
    (_state: RootState, configuration?: TruckConfiguration) => configuration?.apiConfiguration.market,
  ],
  (marketData, marketId) => (marketId ? marketData[marketId] : undefined),
)
